<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

import {
  enhanceMarkdownCollapse,
  markdownCollapse,
  unmountMarkdownCollapse,
} from '@fondof-ui/components/markdown/markdown-collapse'
import { customMarkdownRenderer } from '@fondof-ui/components/markdown/renderer'
import type { HTMLAttributeClass } from '@fondof-ui/types'
import { brandedString } from '@shared/fondof-fn/string/branding'
import { marked } from 'marked'
import sanitizeHtml from 'sanitize-html'

marked.use({
  renderer: customMarkdownRenderer,
  pedantic: true,
  mangle: false,
  headerIds: false,
  gfm: false,
  extensions: [markdownCollapse],
})

const props = defineProps<
  {
    content: string
    setBrand?: boolean
  } & HTMLAttributeClass
>()

const markdownRoot = ref<HTMLElement | null>(null)

const brandedContent = computed(() => {
  if (!props.setBrand) return props.content

  return brandedString(props.content)
})

const parsedMarkdown = computed(() => {
  return sanitizeHtml(marked.parse(brandedContent.value), {
    allowedAttributes: {
      span: ['style', 'class', 'data-markdown-collapse'],
      div: ['style', 'class', 'markdown'],
      a: ['style', 'href', 'target', 'class', 'aria-label'],
      p: ['style', 'class'],
      img: ['src', 'alt', 'style', 'title', 'class'],
      li: ['style', 'class'],
      ul: ['style', 'class'],
      ol: ['style', 'class'],
      iframe: [
        'width',
        'height',
        'style',
        'frameborder',
        'src',
        'onmousewheel',
        'class',
      ],
    },
    allowedTags: [
      'span',
      'div',
      'a',
      'p',
      'img',
      'strong',
      'small',
      'br',
      'section',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'ul',
      'ol',
      'li',
      'iframe',
      'details',
      'summary',
    ],
    allowedStyles: {
      '*': {
        background: [/.*/],
        border: [/.*/],
        // Match HEX and RGB
        color: [
          /^#(0x)?[0-9a-f]+$/i,
          /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/,
        ],
        'text-align': [/^left$/, /^right$/, /^center$/],
        // Match any number with px, em, or %
        'font-size': [/^\d+(?:px|em|%)$/],
        'line-height': [/^\d+(?:px|em|%)?$/, /^normal$/],
        // Allow background-image for icon bullets (only url)
        'background-image': [/^url\(['']?[^'')]+['']?\)$/],
        'background-size': [
          /^cover$/,
          /^contain$/,
          /^auto$/,
          /^\d+(?:px|em|%)$/,
        ],
        width: [/^\d*\.?\d+(px|em|%|rem)$/],
        height: [/^\d*\.?\d+(px|em|%|rem)$/],
        'list-style': [/^none$/],
        'list-style-type': [
          /^none$/,
          /^disc$/,
          /^circle$/,
          /^square$/,
          /^decimal$/,
          /^lower-alpha$/,
          /^upper-alpha$/,
          /^lower-roman$/,
          /^upper-roman$/,
        ],
        display: [/^inline$/, /^inline-block$/, /^block$/, /^flex$/, /^none$/],
        position: [
          /^static$/,
          /^relative$/,
          /^absolute$/,
          /^fixed$/,
          /^sticky$/,
        ],
        padding: [/^\d+(?:px|em|%)$/, /^calc\([^)]*\)$/],
        'padding-left': [/^\d+(?:px|em|%)$/, /^calc\([^)]*\)$/],
        'padding-right': [/^\d+(?:px|em|%)$/, /^calc\([^)]*\)$/],
        'padding-top': [/^\d+(?:px|em|%)$/, /^calc\([^)]*\)$/],
        'padding-bottom': [/^\d+(?:px|em|%)$/, /^calc\([^)]*\)$/],
        cursor: [/^pointer$/],
      },
    },
  })
})

const registerVueComponents = () => {
  const container = markdownRoot.value

  if (!container) return

  enhanceMarkdownCollapse(container)
}

const updateRenderedMarkdown = () => {
  const container = markdownRoot.value
  if (!container) return
  unmountMarkdownCollapse(container)
  container.innerHTML = parsedMarkdown.value
  registerVueComponents()
}

watch(parsedMarkdown, updateRenderedMarkdown)
onMounted(updateRenderedMarkdown)
</script>

<template>
  <span
    ref="markdownRoot"
    :class="props.class"
    v-html="parsedMarkdown"
  />
</template>

<style scoped>
/* styles have to be global, since markdown is rendered outside of component scope */
:deep(ul:has(.markdown-icon-bullet-item)) {
  padding-left: 10px;
}

:deep(.markdown-icon-bullet-item) {
  display: flex;
  align-items: start;
  list-style-type: none;
  position: relative;
  padding-left: 0;
}

:deep(.markdown-icon-bullet) {
  flex-shrink: 0;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  font-size: inherit;
  margin-right: 0.5em;
  position: relative;
  left: 0;
}

:deep(summary) {
  cursor: pointer;
}
</style>
